import type {
  ChildFeeAccount,
  ChildFeePayment,
  FeeAccountCategory,
  FeeBillingPattern,
  ParentChild,
} from "../types/parentFamily";
import { getChildFullName } from "./parentFamilyHelpers";
import { formatLedgerCurrency, formatLedgerDate } from "./schoolFeesHelpers";
import { formatShortDate } from "./parentFamilyHelpers";

export { formatShortDate };

export interface FeeAccountSummary {
  totalDue: number;
  totalPaid: number;
  outstanding: number;
  isSettled: boolean;
  billableMonths: number;
  isActive: boolean;
  statusLabel: string;
}

function parseDateOnly(value?: string): Date | null {
  if (!value?.trim()) return null;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (![y, m, d].every(Number.isFinite)) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function monthsInclusive(from: Date, to: Date): number {
  if (to < from) return 0;
  return (
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth()) +
    1
  );
}

export { todayIsoDate } from "./familyDateUtils";

export function categoryLabel(category: FeeAccountCategory): string {
  switch (category) {
    case "extracurricular":
      return "Extracurricular";
    case "event":
      return "Event";
    default:
      return "Other";
  }
}

export function billingPatternLabel(pattern: FeeBillingPattern): string {
  return pattern === "once-off" ? "Once off / Event" : "Extracurricular (monthly)";
}

export function totalPaid(account: ChildFeeAccount): number {
  return (account.payments || []).reduce((sum, p) => sum + (p.amountPaid || 0), 0);
}

export function effectiveEndDate(
  account: ChildFeeAccount,
  asOf = new Date()
): Date {
  const candidates: Date[] = [monthStart(asOf)];
  const end = parseDateOnly(account.endDate);
  const cancelled = parseDateOnly(account.cancelledAt);
  if (end) candidates.push(monthStart(end));
  if (cancelled) candidates.push(monthStart(cancelled));
  return candidates.reduce((min, d) => (d < min ? d : min));
}

export function billableMonths(
  account: ChildFeeAccount,
  asOf = new Date()
): number {
  const start = parseDateOnly(account.startDate);
  if (!start) return 0;
  const end = effectiveEndDate(account, asOf);
  return monthsInclusive(monthStart(start), end);
}

export function expectedAmount(
  account: ChildFeeAccount,
  asOf = new Date()
): number {
  if (account.paidInFull) {
    return totalPaid(account);
  }
  if (account.billingPattern === "once-off") {
    return account.totalAmount ?? 0;
  }
  const months = billableMonths(account, asOf);
  return months * (account.monthlyAmount ?? 0);
}

export function amountOutstanding(
  account: ChildFeeAccount,
  asOf = new Date()
): number {
  if (account.paidInFull) return 0;
  return Math.max(0, expectedAmount(account, asOf) - totalPaid(account));
}

export function isAccountActive(
  account: ChildFeeAccount,
  asOf = new Date()
): boolean {
  if (account.paidInFull) return false;
  const start = parseDateOnly(account.startDate);
  if (!start || monthStart(asOf) < monthStart(start)) return false;
  const end = parseDateOnly(account.endDate);
  const cancelled = parseDateOnly(account.cancelledAt);
  const asOfMonth = monthStart(asOf);
  if (end && asOfMonth > monthStart(end)) return false;
  if (cancelled && asOfMonth > monthStart(cancelled)) return false;
  return true;
}

export function accountStatusLabel(account: ChildFeeAccount): string {
  if (account.paidInFull || amountOutstanding(account) <= 0) return "Paid up";
  if (account.cancelledAt) return "Cancelled";
  const end = parseDateOnly(account.endDate);
  if (end && monthStart(new Date()) > monthStart(end)) return "Ended";
  if (isAccountActive(account)) return "Active";
  return "Scheduled";
}

export function summarizeFeeAccount(
  account: ChildFeeAccount,
  asOf = new Date()
): FeeAccountSummary {
  const paid = totalPaid(account);
  const due = expectedAmount(account, asOf);
  const outstanding = amountOutstanding(account, asOf);
  return {
    totalDue: due,
    totalPaid: paid,
    outstanding,
    isSettled: account.paidInFull || outstanding <= 0,
    billableMonths: billableMonths(account, asOf),
    isActive: isAccountActive(account, asOf),
    statusLabel: accountStatusLabel(account),
  };
}

export function nextFeeAccountPaymentNumber(account: ChildFeeAccount): number {
  const nums = (account.payments || []).map((p) => p.paymentNumber);
  return nums.length ? Math.max(...nums) + 1 : 1;
}

export function sortFeeAccountPayments(
  payments: ChildFeePayment[]
): ChildFeePayment[] {
  return [...payments].sort((a, b) => a.paymentNumber - b.paymentNumber);
}

export function accountSubtitle(account: ChildFeeAccount): string {
  const summary = summarizeFeeAccount(account);
  const parts = [categoryLabel(account.category), billingPatternLabel(account.billingPattern)];

  if (account.billingPattern === "once-off") {
    if (account.totalAmount != null) {
      parts.push(formatLedgerCurrency(account.totalAmount, account.currency));
    }
    if (account.dueDate) {
      parts.push(`Due ${formatShortDate(account.dueDate)}`);
    }
  } else {
    if (account.monthlyAmount != null) {
      parts.push(`${formatLedgerCurrency(account.monthlyAmount, account.currency)}/mo`);
    }
    if (summary.billableMonths > 0) {
      parts.push(`${summary.billableMonths} mo`);
    }
  }

  parts.push(summary.statusLabel);
  return parts.join(" · ");
}

export function buildFeeAccountNotice(
  child: Pick<ParentChild, "firstName" | "lastName" | "grade" | "classSection">,
  account: ChildFeeAccount
): { title: string; body: string } {
  const childName = getChildFullName(child) || "your child";
  const summary = summarizeFeeAccount(account);
  const paidRows = sortFeeAccountPayments(account.payments);

  const header =
    account.category === "event"
      ? "Payment request"
      : account.category === "extracurricular"
        ? "Extracurricular fees"
        : "Fee statement";

  const bodyLines = [
    header,
    "",
    `Student: ${childName}`,
    `Grade: ${child.grade || "—"}`,
    `Charge: ${account.title}`,
    `Type: ${categoryLabel(account.category)} · ${billingPatternLabel(account.billingPattern)}`,
  ];

  if (account.billingPattern === "once-off") {
    bodyLines.push(
      `Amount: ${formatLedgerCurrency(account.totalAmount ?? 0, account.currency)}`
    );
    if (account.dueDate) {
      bodyLines.push(`Due date: ${formatShortDate(account.dueDate)}`);
    }
  } else {
    bodyLines.push(
      `Monthly fee: ${formatLedgerCurrency(account.monthlyAmount ?? 0, account.currency)}`,
      `Period: ${formatShortDate(account.startDate)}${
        account.endDate ? ` – ${formatShortDate(account.endDate)}` : " – ongoing"
      }`,
      `Billable months: ${summary.billableMonths}`
    );
  }

  if (paidRows.length > 0) {
    bodyLines.push("", "Payments recorded:");
    for (const p of paidRows) {
      bodyLines.push(
        `#${p.paymentNumber} | ${formatLedgerDate(p.paidDate)} | ${formatLedgerCurrency(p.amountPaid, account.currency)} | Receipt ${p.receiptNo}`
      );
    }
  }

  bodyLines.push(
    "",
    `Amount due: ${formatLedgerCurrency(summary.outstanding, account.currency)}`,
    summary.isSettled ? "Status: Paid up — thank you." : "",
    "",
    "Please contact the school office if you have any queries."
  );

  return {
    title: summary.isSettled
      ? `${account.title}: ${childName} — paid up`
      : `${account.title}: ${childName}`,
    body: bodyLines.filter(Boolean).join("\n"),
  };
}

export function defaultEndDateForOneMonth(startDate: string): string {
  const start = parseDateOnly(startDate);
  if (!start) return startDate;
  const end = addMonths(monthStart(start), 0);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, "0");
  const lastDay = new Date(y, end.getMonth() + 1, 0).getDate();
  return `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
}
